import { Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { MailCheck } from 'lucide-react'

import { AuthLayout } from '@/routes/login'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/field'
import { useForgotPassword } from '@/features/auth/api'

const schema = z.object({
  email: z.string().min(1, 'Email wajib diisi.').email('Format email belum benar.'),
})

type FormValues = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const request = useForgotPassword()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  const submit = form.handleSubmit((values) => request.mutate(values.email))

  // Keadaan terkirim ditampilkan tanpa memedulikan apakah alamatnya benar
  // terdaftar. Peladen sengaja tidak membedakan keduanya, dan antarmuka yang
  // membedakannya akan membocorkan persis yang disembunyikan peladen.
  if (request.isSuccess) {
    return (
      <AuthLayout
        title="Periksa kotak masuk Anda"
        subtitle="Tautan atur ulang kata sandi sudah kami kirim."
      >
        <div className="rounded-2xl border border-line p-5">
          <span className="flex size-11 items-center justify-center rounded-xl bg-brand-soft text-brand">
            <MailCheck className="size-5.5" />
          </span>
          <p className="mt-4 leading-relaxed text-muted">
            Bila <span className="font-semibold text-ink">{form.getValues('email')}</span> terdaftar di
            skava.id, tautan atur ulang sudah dikirim ke sana. Tautannya berlaku satu jam dan hanya dapat
            dipakai sekali.
          </p>
          <p className="mt-3 text-sm text-subtle">
            Belum masuk juga setelah beberapa menit? Periksa folder spam, atau minta tautan baru.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => request.reset()}>
              Minta tautan lagi
            </Button>
            <Link to="/masuk">
              <Button variant="ghost" size="sm">
                Kembali ke halaman masuk
              </Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Lupa kata sandi"
      subtitle="Masukkan alamat surel akun Anda, kami kirim tautan untuk menggantinya."
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          placeholder="nama@perusahaan.id"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Button type="submit" fullWidth size="lg" loading={request.isPending}>
          Kirim tautan atur ulang
        </Button>
      </form>

      <p className="mt-5 text-center text-sm text-muted">
        Ingat kata sandinya?{' '}
        <Link to="/masuk" className="font-semibold text-brand hover:underline">
          Masuk di sini
        </Link>
      </p>
    </AuthLayout>
  )
}
